import React from "react";
import Sidebar from "../../Navigation/Sidebar/Sidebar";
import varifiedIcon from "../../../assests/verifiedIcon.svg";
import { Upload, X, Plus, Trash2 } from "lucide-react";
import "./NavigationalForm.css";

const NavigationalCreateComponent = ({
  formData,
  setFormData,
  iconPreview,
  bgPreview,
  useTransparentBg,
  variantIds,
  variantDetails,
  inputValue,
  setInputValue,
  iconInputRef,
  backgroundInputRef,
  isFormValid,
  isSubmitting,
  isIconUploading,
  isBgUploading,
  showSuccessPopup,
  setShowSuccessPopup,
  handleToggleTransparent,
  handleAddId,
  removeId,
  handleClearAllVariants,
  handleBackClick,
  handleSave,
  handleIconFileSelect,
  handleBgFileSelect,
  handleClearIcon,
  handleClearBg,
  iconPath,
  bgPath,
  createdItemName,
  createdItemId,
  isVerifying,
}) => {
  const S3_BASE_URL = "https://s3.ap-south-1.amazonaws.com/daalchini/dcprod/";

  const getVariantImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${S3_BASE_URL}${imagePath.replace(/^\//, '')}`;
  };

  return (
    <div className="nav-create-container">
      <Sidebar />
      
      <div className="nav-create-content">
        {/* Fixed Header */}
        <div className="nav-create-header">
          <button onClick={handleBackClick} className="nav-create-back-btn">←</button>
          <h1>Create Navigational</h1>
        </div>

        {/* Scrollable Content */}
        <div className="nav-create-scrollable">
          <div className="nav-create-card">
            <div className="nav-create-inner">
              {/* Mapping Type */}
              <div className="nav-create-mapping-indicator">
                <label>Current Mapping Type</label>
                <div className="nav-create-mapping-badge">
                  <div className="nav-create-mapping-dot"></div>
                  Variant
                </div>
              </div>

              {/* Name Field */}
              <div className="nav-create-form-field">
                <label>
                  Enter Navigational Name <span className="nav-create-required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="nav-create-form-input"
                  placeholder="Eg. Fresh Food, Electronics and Accessories, Snacks"
                />
              </div>

              {/* Description Field */}
              <div className="nav-create-form-field">
                <label>Enter Description (Optional)</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="nav-create-form-input"
                  placeholder="Eg. Details of the product"
                />
              </div>

              {/* Icon Upload - Optional */}
              <div className="nav-create-form-field">
                <label>Icon (Optional)</label>
                <div className="nav-create-upload-wrapper">
                  <div className="nav-create-upload-container">
                    <input
                      type="file"
                      ref={iconInputRef}
                      style={{ display: "none" }}
                      accept="image/*"
                      onChange={handleIconFileSelect}
                    />
                    <div className="nav-create-upload-area" onClick={() => iconInputRef.current?.click()}>
                      {iconPreview ? (
                        <div className="nav-create-preview-wrapper">
                          <img src={iconPreview} alt="Icon preview" className="nav-create-preview-icon" />
                          <button
                            className="nav-create-clear-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClearIcon();
                            }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="nav-create-upload-content">
                          <Upload size={32} className="nav-create-upload-icon" />
                          <p>Choose Icon Image</p>
                          <small>Click to select (Optional)</small>
                        </div>
                      )}
                    </div>
                  </div>
                  {isIconUploading && (
                    <div className="nav-create-uploading-indicator">
                      <div className="nav-create-spinner-small"></div>
                      <span>Uploading...</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Background Upload - Optional */}
              <div className="nav-create-form-field">
                <label>Background Image (Optional)</label>

                <label className="nav-create-checkbox-label">
                  <input
                    type="checkbox"
                    checked={useTransparentBg}
                    onChange={handleToggleTransparent}
                  />
                  <span>Use Transparent Background Image</span>
                </label>

                {/* Show upload section only when NOT using transparent background */}
                {!useTransparentBg && (
                  <div className="nav-create-upload-wrapper">
                    <div className="nav-create-upload-container">
                      <input
                        type="file"
                        ref={backgroundInputRef}
                        style={{ display: "none" }}
                        accept="image/*"
                        onChange={handleBgFileSelect}
                      />
                      <div className="nav-create-upload-area" onClick={() => backgroundInputRef.current?.click()}>
                        {bgPreview ? (
                          <div className="nav-create-preview-wrapper-bg">
                            <img src={bgPreview} alt="Background preview" className="nav-create-preview-bg" />
                            <button
                              className="nav-create-clear-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClearBg();
                              }}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="nav-create-upload-content">
                            <Upload size={32} className="nav-create-upload-icon" />
                            <p>Choose Background Image</p>
                            <small>Click to select (Optional)</small>
                          </div>
                        )}
                      </div>
                    </div>
                    {isBgUploading && (
                      <div className="nav-create-uploading-indicator">
                        <div className="nav-create-spinner-small"></div>
                        <span>Uploading...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Variant IDs Block - Modern Design */}
              <div className="nav-create-variant-section">
                <div className="nav-create-variant-header">
                  <label>Add Variant IDs</label>
                  <span className="nav-create-variant-count"> {variantIds.length} Variant IDs added</span>
                </div>

                {/* Add Variant Input */}
                <div className="nav-create-add-variant">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddId()}
                    placeholder="Enter IDs separated by space or comma"
                    disabled={isVerifying}
                  />
                  <button onClick={handleAddId} disabled={isVerifying || !inputValue.trim()}>
                    {isVerifying ? "Verifying..." : "Add"}
                  </button>
                </div>
                
                {variantIds.length > 0 && (
                  <div className="nav-create-variant-preview-box">
                    <div className="nav-create-preview-header">
                      <div className="nav-create-preview-title-group">
                        <div className="nav-create-live-dot"></div>
                        <span className="nav-create-preview-title">VARIANTS PREVIEW</span>
                      </div>
                      <span className="nav-create-preview-count">{variantIds.length} ITEMS</span>
                    </div>

                    <div className="nav-create-preview-grid">
                      {variantIds.map((id) => {
                        const details = variantDetails[id];
                        return (
                          <div key={id} className="nav-create-preview-item">
                            <div className="nav-create-item-thumb">
                              {details?.image ? (
                                <img 
                                  src={getVariantImageUrl(details.image)} 
                                  alt={`Variant ${id}`}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div className="nav-create-placeholder-img"></div>
                              )}
                            </div>
                            <div className="nav-create-item-info">
                              <span className="nav-create-item-name">Variant Product {id}</span>
                              <span className="nav-create-item-id">ID: {id}</span>
                            </div>
                            <button 
                              className="nav-create-item-remove"
                              onClick={() => removeId(id)}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              {variantIds.length > 0 && (
                <div className="nav-create-clear-variants">
                  <button onClick={handleClearAllVariants} className="nav-create-clear-variants-btn">
                    <Trash2 size={14} /> Clear All Variants
                  </button>
                </div>
              )}
              
              {/* Status Buttons */}
              <div className="nav-create-status-section">
                <div className="nav-create-status-buttons">
                  <button
                    onClick={() => setFormData({ ...formData, active: true })}
                    className={`nav-create-status-btn ${formData.active === true ? "nav-create-status-active" : ""}`}
                  >
                    ACTIVE
                  </button>
                  <button
                    onClick={() => setFormData({ ...formData, active: false })}
                    className={`nav-create-status-btn ${formData.active === false ? "nav-create-status-inactive" : ""}`}
                  >
                    INACTIVE
                  </button>
                </div>
              </div>

              {/* Save Button - Enabled only when name is entered */}
              <div className="nav-create-save-section">
                <button
                  onClick={handleSave}
                  disabled={!formData.name.trim() || isSubmitting}
                  className={`nav-create-save-btn ${formData.name.trim() ? "nav-create-active" : ""}`}
                >
                  {isSubmitting ? "Creating..." : "Add Navigational"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="nav-success-overlay">
          <div className="nav-success-modal">
            <div className="nav-success-icon">
              <img style={{width:"100%", height:"100%"}} src={varifiedIcon} alt="" />
            </div>
            <h3 className="nav-success-title">Created Successfully!</h3>
            <p className="nav-success-message">Navigational has been created successfully.</p>
            <div className="nav-success-details">
              <div className="nav-success-detail-row">
                <span className="nav-success-label">Name:</span>
                <span className="nav-success-value">{createdItemName}</span>
              </div>
              <div className="nav-success-detail-row">
                <span className="nav-success-label">ID:</span>
                <span className="nav-success-value">{createdItemId}</span>
              </div>
            </div>
            <button className="nav-success-btn" onClick={() => setShowSuccessPopup(false)}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavigationalCreateComponent;